<template>

    <ThemeChanger>

        <Container v-if="isLoading" fluid class="pt-52 pb-24">

            <Grid>

                <Column :span="[ 2, 6 ]">

                    <div class="skeleton h-10 md:h-12 w-full md:w-5/6"/>

                    <div class="skeleton mt-12 md:mt-20 h-4 w-full md:w-3/6"/>

                </Column>

                <Column :span="[ 2, 2 ]" class="mt-24">

                    <div class="skeleton h-4"/>

                </Column>

                <Column :span="[ 2, 2 ]" class="mt-12 md:mt-24">

                    <div class="skeleton h-4"/>

                </Column>

                <Column :span="[ 2, 2 ]" class="mt-12 md:mt-24">

                    <div class="skeleton h-4"/>

                </Column>

                <Column :span="[ 2, 6 ]" class="mt-32">

                    <div class="skeleton h-64 md:h-96"/>

                </Column>

                <Column :span="[ 2, 4 ]" :push="[ 0, 1 ]" class="mt-32">

                    <div class="skeleton h-4"/>

                    <div class="skeleton mt-8 h-4"/>

                    <div class="skeleton mt-8 h-4"/>

                </Column>

            </Grid>

        </Container>

        <template v-else>

            <Container fluid class="pt-48 pb-24">

                <Grid>

                    <Column :span="[ 2, 4 ]">

                        <Heading tag="h1" v-html="package.title"/>

                        <Paragraph v-if="package.description" v-html="package.description"/>

                    </Column>

                </Grid>

                <Grid class="mt-20">

                    <Column :span="2">

                        <Paragraph class="font-bold pb-5 border-b" v-html="package.category.title"/>

                    </Column>

                    <Column :span="2">

                        <Paragraph class="font-bold sm:pt-5 pb-5 border-b">
                            {{ isCNLocale ? `${package.downloads}的下载` : `${package.downloads} Downloads` }}
                        </Paragraph>

                    </Column>

                    <Column :span="2">

                        <Paragraph class="font-bold sm:pt-5 pb-5 border-b">
                            {{ isCNLocale ? `${package.stars}的星标` : `${package.stars} Stars` }}
                        </Paragraph>

                    </Column>

                </Grid>

                <Grid class="pt-24" v-if="package.image">

                    <Picture auto cover :image="package.image"/>

                </Grid>

                <Grid class="pt-24">

                    <Column :span="[ 2, 4 ]" :push="[ 0, 1 ]">

                        <Paragraph v-html="package.introduction"/>

                    </Column>

                </Grid>

                <Grid class="mt-20">

                    <Column :span="[ 2, 4 ]" :push="[ 0, 1 ]">

                        <Heading tag="h2">
                            {{ isCNLocale ? '特征' : 'Features' }}
                        </Heading>

                    </Column>

                </Grid>

                <TitleAndList :list="package.features" class="mt-7 md:mt-12"/>

                <Grid class="mt-24" v-if="Object.keys(package?.license_content).length > 0">

                    <Column :span="[ 2, 4 ]" :push="[ 0, 1 ]">

                        <Heading tag="h3" v-html="package.license_heading" v-if="package.license_heading"/>

                        <DynamicContent class="mt-5" :content="package.license_content"/>

                    </Column>

                </Grid>

            </Container>

        </template>

    </ThemeChanger>

    <ThemeChanger :to="Theme.Dark">

        <Container fluid class="pt-24" v-if="package.category">

            <Grid>

                <Column :span="[ 2, 4 ]" :push="[ 0, 1 ]">

                    <Heading tag="h3" v-html="package?.category?.about_heading"/>

                    <DynamicContent class="mt-5" :content="package?.category?.about_content"/>

                </Column>

            </Grid>

            <Grid class="mt-24">

                <Column :span="[ 2, 4 ]" :push="[ 0, 1 ]" v-if="package.category.custom_package_label && package.category.custom_package_content">

                    <Notice :title="package.category.custom_package_label" :description="package.category.custom_package_content"/>

                </Column>

                <Column :span="[ 2, 4 ]" :push="[ 0, 1 ]" class="mt-16" v-if="package.category.build_app_label && package.category.build_app_content">

                    <Notice :title="package.category.build_app_label" :description="package.category.build_app_content"/>

                </Column>

            </Grid>

        </Container>

    </ThemeChanger>

    <MetaInfo v-if="package.seo" :seo="package.seo" :structured-data="package.seo_structured_data"/>

</template>

<script lang="ts">
    import { defineComponent, onBeforeMount, ref } from 'vue'
    import Grid from '../components/Grid/Grid.vue'
    import Column from '../components/Grid/Column.vue'
    import Container from '../components/Container.vue'
    import DynamicContent from '../components/DynamicContent.vue'
    import Heading from '../components/Heading.vue'
    import Notice from '../components/Notice.vue'
    import Paragraph from '../components/Paragraph.vue'
    import Picture from '../components/Picture.vue'
    import MetaInfo from '../components/MetaInfo.vue'
    import ThemeChanger from '../components/ThemeChanger.vue'
    import TitleAndList from '../components/TitleAndList.vue'
    import { useTheme } from '../compositions/UseTheme'
    import { useApi } from '../compositions/UseApi'
    import { Theme } from '../enums/Theme'
    import { useRoute } from 'vue-router'
    import { useCms } from '../compositions/UseCms'

    export default defineComponent({
        name: 'OpenSourceSingle',
        components: {
            Grid,
            Column,
            Container,
            DynamicContent,
            Heading,
            Notice,
            Paragraph,
            Picture,
            MetaInfo,
            ThemeChanger,
            TitleAndList
        },
        setup() {

            useTheme().initializeCurrentThemeBasedOnRouteMeta()
            const route = useRoute()
            const { isCNLocale } = useCms()

            const packageData = ref<any>({})
            const isLoading = ref(true)
            const isFailed = ref(false)

            onBeforeMount(async() => {

                const id = route.params.slug as string

                const response = await useApi().openSourcePackage(id, isCNLocale.value ? 'cn' : 'en')

                if (response.isOk()) {

                    isLoading.value = false
                    packageData.value = response.value.data

                }

                if (response.isErr()) {

                    isLoading.value = false
                    isFailed.value = true

                }

            })

            return {
                package: packageData,
                isLoading,
                isFailed,
                isCNLocale,
                Theme
            }

        }
    })
</script>
